import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { products, type Product } from "@/mocks/products";
import { testimonials } from "@/mocks/testimonials";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";

function Breadcrumb({ product }: { product: Product }) {
  return (
    <nav className="flex items-center gap-2 text-sm text-dark-400 mb-8 flex-wrap">
      <Link to="/" className="hover:text-dark-900 transition-colors whitespace-nowrap">
        Početna
      </Link>
      <i className="ri-arrow-right-s-line text-xs"></i>
      <Link to="/products" className="hover:text-dark-900 transition-colors whitespace-nowrap">
        Proizvodi
      </Link>
      <i className="ri-arrow-right-s-line text-xs"></i>
      <Link
        to={`/products?category=${product.categorySlug}`}
        className="hover:text-dark-900 transition-colors whitespace-nowrap"
      >
        {product.category}
      </Link>
      <i className="ri-arrow-right-s-line text-xs"></i>
      <span className="text-dark-700 font-medium whitespace-nowrap">{product.name}</span>
    </nav>
  );
}

function ImageGallery({ product }: { product: Product }) {
  const [selectedImage, setSelectedImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const allImages = [product.image, ...product.gallery];

  useEffect(() => {
    setSelectedImage(0);
  }, [product.id]);

  const handlePrev = () => {
    setSelectedImage((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  };
  const handleNext = () => {
    setSelectedImage((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
  };

  return (
    <>
      <div className="space-y-4">
        <div className="relative aspect-square bg-dark-50/50 rounded-3xl overflow-hidden group cursor-pointer">
          <img
            src={allImages[selectedImage]}
            alt={`${product.name} - slika ${selectedImage + 1}`}
            className="w-full h-full object-contain object-center"
            onClick={() => setLightboxOpen(true)}
          />
          <button
            onClick={(e) => { e.stopPropagation(); handlePrev(); }}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/90 text-dark-900 hover:bg-white transition-all opacity-0 group-hover:opacity-100 shadow-sm cursor-pointer"
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleNext(); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/90 text-dark-900 hover:bg-white transition-all opacity-0 group-hover:opacity-100 shadow-sm cursor-pointer"
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {allImages.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setSelectedImage(i); }}
                className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                  i === selectedImage ? "bg-primary-500 w-6" : "bg-dark-300 hover:bg-dark-400"
                }`}
              />
            ))}
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {allImages.map((img, i) => (
            <button
              key={i}
              onClick={() => setSelectedImage(i)}
              className={`aspect-square bg-dark-50/50 rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                i === selectedImage
                  ? "border-primary-500"
                  : "border-transparent hover:border-dark-200"
              }`}
            >
              <img
                src={img}
                alt={`${product.name} thumbnail ${i + 1}`}
                className="w-full h-full object-contain object-center"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      </div>

      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer z-10"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handlePrev(); }}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleNext(); }}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
          <img
            src={allImages[selectedImage]}
            alt={`${product.name} - slika ${selectedImage + 1}`}
            className="max-w-[90vw] max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 text-sm">
            {selectedImage + 1} / {allImages.length}
          </div>
        </div>
      )}
    </>
  );
}

function ProductInfo({ product }: { product: Product }) {
  return (
    <div className="flex flex-col justify-center">
      <span className="inline-block text-xs font-semibold text-accent-600 uppercase tracking-wider mb-3 bg-accent-100/80 px-3 py-1 rounded-full w-fit">
        {product.category}
      </span>

      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-dark-900 leading-tight mb-4">
        {product.name}
      </h1>

      <p className="text-dark-500 text-base leading-relaxed mb-6">
        {product.longDesc}
      </p>

      <div className="flex items-baseline gap-3 mb-8">
        <span className="text-4xl font-bold text-dark-900">{product.price}</span>
        <span className="text-sm text-dark-400">sa PDV-om</span>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-10">
        <a
          href="#contact"
          className="bg-primary-500 hover:bg-primary-600 text-white px-8 py-4 rounded-full text-base font-semibold transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-mail-send-line"></i>
          Zatražite ponudu
        </a>
        <a
          href="tel:+381112345678"
          className="bg-dark-900 hover:bg-dark-800 text-white px-8 py-4 rounded-full text-base font-semibold transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-phone-line"></i>
          Pozovite odmah
        </a>
        <button
          onClick={() => {
            const el = document.getElementById("contact");
            if (el) el.scrollIntoView({ behavior: "smooth" });
          }}
          className="border-2 border-dark-200 text-dark-900 hover:bg-dark-50 px-8 py-4 rounded-full text-base font-semibold transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-download-line"></i>
          Preuzmite brošuru
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4 p-6 bg-dark-50/50 rounded-2xl border border-dark-100">
        <div className="text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 bg-accent-100 rounded-full">
            <i className="ri-truck-line text-accent-600"></i>
          </div>
          <span className="block text-xs font-semibold text-dark-900">Besplatna dostava</span>
          <span className="block text-xs text-dark-400 mt-0.5">za porudžbine preko 20,000 RSD</span>
        </div>
        <div className="text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 bg-accent-100 rounded-full">
            <i className="ri-shield-check-line text-accent-600"></i>
          </div>
          <span className="block text-xs font-semibold text-dark-900">3 god. garancije</span>
          <span className="block text-xs text-dark-400 mt-0.5">na svu opremu</span>
        </div>
        <div className="text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 bg-accent-100 rounded-full">
            <i className="ri-tools-line text-accent-600"></i>
          </div>
          <span className="block text-xs font-semibold text-dark-900">Profesionalna montaža</span>
          <span className="block text-xs text-dark-400 mt-0.5">licencirani tim</span>
        </div>
      </div>
    </div>
  );
}

function FeatureList({ features }: { features: string[] }) {
  return (
    <section className="py-16 bg-dark-50/50">
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block text-xs font-semibold text-accent-600 uppercase tracking-widest mb-3 px-4 py-1.5 border border-accent-200 rounded-full">
              Ključne karakteristike
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-dark-900">
              Šta čini ovaj proizvod posebnim
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {features.map((feat, i) => (
              <div
                key={i}
                className="group bg-white rounded-2xl border border-dark-100 p-6 hover:border-primary-200 transition-all duration-300 cursor-default"
              >
                <span className="text-3xl font-bold text-dark-200 group-hover:text-primary-200 transition-colors">
                  {(i + 1).toString().padStart(2, "0")}
                </span>
                <p className="text-dark-700 font-medium mt-3 text-base">{feat}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Benefits({ product }: { product: Product }) {
  return (
    <section className="py-16 bg-white">
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block text-xs font-semibold text-accent-600 uppercase tracking-widest mb-3">
              Šta dobijate
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-dark-900">
              Prednosti koje prave razliku
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {product.benefits.map((benefit, i) => (
              <div key={i} className="bg-dark-50/50 rounded-2xl border border-dark-100 p-6 md:p-8 text-center group hover:-translate-y-1 transition-all duration-300">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-5 bg-primary-100 rounded-xl">
                  <i className={`${benefit.icon} text-primary-600 text-xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-dark-900 mb-3">{benefit.title}</h3>
                <p className="text-sm text-dark-400 leading-relaxed">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TechSpecs({ product }: { product: Product }) {
  return (
    <section className="py-16 bg-dark-50/50">
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-dark-900 mb-4">Tehničke specifikacije</h2>
            <p className="text-dark-400 text-base">Detaljne tehničke informacije za profesionalce</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {product.specs.map((spec, i) => (
              <div
                key={i}
                className="flex items-center justify-between p-5 rounded-xl bg-white border border-dark-100"
              >
                <span className="text-sm text-dark-400 font-medium">{spec.label}</span>
                <span className="text-sm font-semibold text-dark-900 text-right ml-4">{spec.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TestimonialCard() {
  const testimonial = testimonials[0];
  if (!testimonial) return null;

  return (
    <section className="py-16 bg-white">
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="bg-dark-900 rounded-3xl overflow-hidden flex flex-col md:flex-row">
            <div className="w-full md:w-2/5 bg-dark-800 flex items-center justify-center p-8 md:p-12">
              <div className="w-32 h-32 md:w-48 md:h-48 flex items-center justify-center rounded-full bg-dark-700 overflow-hidden">
                <img
                  src={testimonial.avatar}
                  alt={testimonial.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="w-full md:w-3/5 p-8 md:p-12 flex flex-col justify-center">
              <span className="text-6xl text-accent-400 leading-none mb-4 font-serif">&ldquo;</span>
              <blockquote className="text-white/90 text-lg leading-relaxed mb-6">
                {testimonial.text}
              </blockquote>
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-white font-semibold">{testimonial.name}</p>
                  <p className="text-white/50 text-sm">{testimonial.role}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function RelatedProducts({ currentProduct }: { currentProduct: Product }) {
  const related = products
    .filter((p) => p.id !== currentProduct.id && p.category === currentProduct.category)
    .slice(0, 4);

  if (related.length === 0) return null;

  return (
    <section className="py-16 bg-dark-50/50">
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-dark-900">
              Povezani proizvodi
            </h2>
            <p className="text-dark-400 mt-3 text-base">
              Iz iste kategorije &mdash; {currentProduct.category}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {related.map((rp) => (
              <Link
                key={rp.id}
                to={`/product/${rp.id}`}
                className="group bg-white rounded-2xl border border-dark-100 overflow-hidden transition-all duration-300 hover:-translate-y-2"
              >
                <div className="aspect-square bg-dark-50/50 flex items-center justify-center p-6 overflow-hidden">
                  <img
                    src={rp.image}
                    alt={rp.name}
                    className="w-full h-full object-contain object-center group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-base font-bold text-dark-900 group-hover:text-primary-600 transition-colors mb-2">
                    {rp.name}
                  </h3>
                  <span className="text-base font-bold text-dark-900">{rp.price}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CTAForm() {
  return (
    <section id="contact" className="py-20 md:py-28 bg-white">
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-block text-xs font-semibold text-accent-600 uppercase tracking-widest mb-4">
            Kontakt
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-dark-900 mb-4">
            Zainteresovani ste?
          </h2>
          <p className="text-dark-400 text-base mb-10 max-w-xl mx-auto">
            Pošaljite nam upit i naš tim će vas kontaktirati u roku od 24 sata sa personalizovanom ponudom.
          </p>
          <form
            data-readdy-form=""
            data-submit-addr="https://readdy.ai/api/form/d8har25iodfui947ttug"
            id="product-inquiry-form"
            onSubmit={async (e) => {
              e.preventDefault();
              const form = e.target as HTMLFormElement;
              try {
                await fetch("https://readdy.ai/api/form/d8har25iodfui947ttug", {
                  method: "POST",
                  headers: { "Content-Type": "application/x-www-form-urlencoded" },
                  body: new URLSearchParams(new FormData(form) as unknown as Record<string, string>).toString(),
                });
                alert("Poruka je uspešno poslata! Kontaktiraćemo vas u roku od 24 sata.");
                form.reset();
              } catch {
                alert("Došlo je do greške. Molimo pokušajte ponovo ili nas pozovite direktno.");
              }
            }}
            className="bg-dark-50/50 rounded-3xl border border-dark-100 p-6 md:p-10 text-left space-y-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-dark-700 mb-1.5" htmlFor="inq-name">
                  Ime i prezime *
                </label>
                <input
                  id="inq-name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-dark-200 bg-white text-dark-900 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="Vaše ime"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-dark-700 mb-1.5" htmlFor="inq-email">
                  Email adresa *
                </label>
                <input
                  id="inq-email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-dark-200 bg-white text-dark-900 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="email@primer.rs"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-dark-700 mb-1.5" htmlFor="inq-phone">
                Telefon
              </label>
              <input
                id="inq-phone"
                name="phone"
                type="tel"
                className="w-full px-4 py-3 rounded-xl border border-dark-200 bg-white text-dark-900 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                placeholder="+381 6X XXX XXXX"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-dark-700 mb-1.5" htmlFor="inq-message">
                Poruka *
              </label>
              <textarea
                id="inq-message"
                name="message"
                rows={4}
                required
                maxLength={500}
                className="w-full px-4 py-3 rounded-xl border border-dark-200 bg-white text-dark-900 text-sm focus:outline-none focus:border-primary-400 transition-colors resize-none"
                placeholder="Opišite vaše potrebe, vrstu objekta, broj kamera..."
              />
              <p className="text-xs text-dark-400 mt-1.5">Maksimalno 500 karaktera</p>
            </div>
            <button
              type="submit"
              className="bg-primary-500 hover:bg-primary-600 text-white px-8 py-4 rounded-full text-base font-semibold transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer w-full sm:w-auto"
            >
              <i className="ri-send-plane-line"></i>
              Pošaljite upit
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const product = products.find((p) => p.id === id);

  if (!product) {
    return (
      <main className="min-h-screen bg-white">
        <Navbar />
        <section className="pt-24 md:pt-32 pb-20 text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto bg-dark-100 rounded-full mb-4">
            <i className="ri-error-warning-line text-dark-400 text-2xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-dark-900 mb-2">Proizvod nije pronađen</h1>
          <p className="text-dark-400 mb-6">Proizvod koji tražite ne postoji ili je uklonjen.</p>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Nazad na proizvode
          </Link>
        </section>
        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-white">
      <Navbar />

      <section className="pt-24 md:pt-32 pb-0">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb product={product} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 mb-16">
              <ImageGallery product={product} />
              <ProductInfo product={product} />
            </div>
          </div>
        </div>
      </section>

      <FeatureList features={product.features} />
      <Benefits product={product} />
      <TechSpecs product={product} />
      <TestimonialCard />
      <RelatedProducts currentProduct={product} />
      <CTAForm />
      <Footer />
    </main>
  );
}